import { EvilIcons, MaterialIcons } from "@expo/vector-icons";
import moment from "moment";
import React, { useCallback, useContext, useMemo, useRef } from "react";
import {
  FlatList,
  Platform,
  StyleSheet,
  TouchableNativeFeedback,
  TouchableOpacity,
  View,
} from "react-native";
import { ListItem, useTheme } from "react-native-elements";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import {
  addOccurrenceToQueue,
  EventOccurrenceData,
  getEventMessageById,
} from "../../backend/events/Events";
import {
  PlayerContext,
  PLAYER_INITIAL_STATE,
} from "../../backend/PlayerContext";
import { clone } from "../../utils/CoreUtils";
import CategoryContainer from "../utility/CategoryContainer";
import Divider from "../utility/Divider";
import { useGestureHandler } from "../utility/Miscellaneous";
import { Text } from "../utility/Text";

const PLAYER_INITIAL_STATE_CLONED = clone(PLAYER_INITIAL_STATE);

function EventListItem(props: {
  item: EventOccurrenceData;
  onPress: () => void;
}) {
  if (!props.item.currentMessageId) {
    return null;
  }
  const currentMessageData = getEventMessageById(props.item.currentMessageId);
  if (!currentMessageData) {
    return null;
  }
  const title = currentMessageData.getTitle(props.item?.params);
  const playerContext = useContext(PlayerContext);
  const description = useMemo(
    () =>
      moment(props.item.timestamp).format("h:mm A") +
      " - " +
      currentMessageData.getDescription(
        PLAYER_INITIAL_STATE_CLONED,
        props.item?.params,
      ),
    [props.item.timestamp],
  );
  const theme = useTheme();
  return useMemo(() => {
    return (
      <View>
        <ListItem
          onPress={props.onPress}
          Component={Platform.select<any>({
            android: TouchableNativeFeedback,
            default: TouchableOpacity,
          })}
          accessible={true}
          accessibilityLabel={title}
          accessibilityRole="button"
        >
          {/* @ts-expect-error accessibilityHidden is supported in web */}
          <ListItem.Content accessibilityHidden={true} focusable={false}>
            <View style={styles.eventTitleContainer}>
              {currentMessageData.isActionable(
                playerContext,
                props.item?.params,
              ) && (
                <View style={styles.actionableIcon}>
                  <EvilIcons
                    name="exclamation"
                    type="evilicon"
                    size={24}
                    color={theme.theme.colors.black}
                  />
                </View>
              )}
              <ListItem.Title style={styles.titleText}>{title}</ListItem.Title>
            </View>
            <Text numberOfLines={1}>{description}</Text>
          </ListItem.Content>
          <ListItem.Chevron
            name={currentMessageData.isTerminal() ? "check" : "chevron-right"}
            type="material"
            /* @ts-expect-error accessibilityHidden is supported in web */
            accessibilityHidden={true}
          />
        </ListItem>
        <Divider />
      </View>
    );
  }, [
    title,
    description,
    currentMessageData.isTerminal(),
    currentMessageData.isActionable(playerContext, props.item?.params),
    theme,
  ]);
}

const EventListItemMemoized = React.memo(
  EventListItem,
  (prevProps, nextProps) => {
    return prevProps.item.currentMessageId === nextProps.item.currentMessageId;
  },
);

export function EventList(props: {
  style?: any;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
}) {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;

  const keyExtractor = useCallback(
    (item: EventOccurrenceData, index: number) => item.eventOccurrenceId,
    [],
  );

  const renderItem = useCallback((props: any) => {
    const onPress = () => {
      playerContextRef.current.apply(
        addOccurrenceToQueue(props.item.eventOccurrenceId),
      );
    };
    return <EventListItemMemoized {...props} onPress={onPress} />;
  }, []);

  const sortedEvents = Object.values(playerContext.eventOccurrences).sort(
    (a, b) => b.timestamp - a.timestamp,
  );

  const stringifiedEvents = useMemo(
    () => JSON.stringify(playerContext.eventOccurrences),
    [sortedEvents],
  );

  const terminalEvents = useMemo(
    () =>
      sortedEvents
        .filter((event) => {
          return (
            event.currentMessageId &&
            getEventMessageById(event.currentMessageId).isTerminal()
          );
        })
        .slice(0, 20),
    [stringifiedEvents],
  );
  const ongoingEvents = useMemo(
    () =>
      sortedEvents.filter((event) => {
        return (
          event.currentMessageId &&
          !getEventMessageById(event.currentMessageId).isTerminal()
        );
      }),
    [stringifiedEvents],
  );

  const eventCategories = useMemo(() => {
    const eventCategories = [];
    if (ongoingEvents.length > 0) {
      eventCategories.push({ title: "Ongoing", data: ongoingEvents });
    }
    if (terminalEvents.length > 0) {
      eventCategories.push({ title: "Finished", data: terminalEvents });
    }
    return eventCategories;
  }, [ongoingEvents, terminalEvents]);

  const categoryKeyExtractor = useCallback(
    (item: { title: string; data: EventOccurrenceData[] }, index: number) =>
      item.title,
    [],
  );

  const categoryRenderItem = useCallback(
    (props: any) => {
      return (
        <CategoryContainer
          titleStyle={styles.categoryTitle}
          title={props.item.title}
        >
          <FlatList
            keyExtractor={keyExtractor}
            data={props.item.data}
            renderItem={renderItem}
          />
        </CategoryContainer>
      );
    },
    [keyExtractor, renderItem],
  );

  const swipeHandlerGesture = useGestureHandler(
    props.onSwipeLeft,
    props.onSwipeRight,
  );
  const nativeGesture = useMemo(() => Gesture.Native(), []);
  const compositeGesture = useMemo(
    () => Gesture.Simultaneous(swipeHandlerGesture, nativeGesture),
    [swipeHandlerGesture, nativeGesture],
  );

  const theme = useTheme();

  return useMemo(
    () => (
      <View style={props.style}>
        <View
          accessible={true}
          accessibilityLabel="Events"
          accessibilityRole="header"
          style={styles.emptyTitleContainer}
        >
          <MaterialIcons
            size={24}
            name="notifications"
            style={styles.titleIcon}
            color={theme.theme.colors.black}
          />
          <Text h2>Events</Text>
        </View>
        <GestureDetector key="eventGesture" gesture={compositeGesture}>
          <FlatList
            keyExtractor={categoryKeyExtractor}
            data={eventCategories}
            renderItem={categoryRenderItem}
          />
        </GestureDetector>
      </View>
    ),
    [
      theme,
      categoryKeyExtractor,
      categoryRenderItem,
      eventCategories,
      compositeGesture,
    ],
  );
}

const styles = StyleSheet.create({
  categoryTitle: {
    marginLeft: 8,
  },
  emptyTitleContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 8,
  },
  titleIcon: {
    paddingRight: 8,
    height: 28,
  },
  eventTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  titleText: {
    flex: 1,
  },
  actionableIcon: {
    marginRight: 4,
  },
});
